import { useEffect, useRef, useState } from 'react';

import { SESSIONS_RENDER_STEP } from '@/constants';

import type { Session } from '@/types';

type SessionGrouping = Record<string, Session[]>;

const countSessions = (grouping: SessionGrouping) =>
  Object.values(grouping).reduce((sum, bucket) => sum + bucket.length, 0);

// The first `limit` sessions in bucket order, with the grouping preserved so
// the month headers the list renders stay attached to their rows.
const takeSessions = (grouping: SessionGrouping, limit: number) => {
  const visible: SessionGrouping = {};
  let remaining = limit;
  Object.entries(grouping).forEach(([bucket, rows]) => {
    if (remaining <= 0) return;
    const slice = rows.slice(0, remaining);
    if (slice.length) visible[bucket] = slice;
    remaining -= slice.length;
  });
  return visible;
};

// MEN-4928: the sessions page is one long scroll over a complete list, so the
// row count is unbounded — a coach's history runs to thousands. `SessionList`
// renders a card per session with no virtualization, so mounting every row at
// once is the cost the scroll was meant to avoid. Reveal a bounded window and
// grow it when the sentinel scrolls into view.
//
// This bounds rendering only. The full list is already in memory, so the
// window never has to wait on a fetch and reconciliation upstream still sees
// every row.
export const useIncrementalSessions = (
  grouping: SessionGrouping,
  resetKey: string,
  step: number = SESSIONS_RENDER_STEP,
) => {
  const [visibleCount, setVisibleCount] = useState(step);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Restart the window when the viewer or subject changes. `grouping` is a
  // fresh object on every render, so it cannot itself be the reset trigger.
  useEffect(() => {
    setVisibleCount(step);
  }, [resetKey, step]);

  const hasMore = countSessions(grouping) > visibleCount;

  useEffect(() => {
    // Absent in jsdom and in any environment without the API; the window then
    // stays at its initial size rather than throwing.
    if (typeof IntersectionObserver === 'undefined') return undefined;
    if (!hasMore) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setVisibleCount((count) => count + step);
      });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, step]);

  return {
    hasMore,
    sentinelRef,
    visibleSessions: takeSessions(grouping, visibleCount),
  };
};
